/**
 * Fire `callback` once, the first time `element` enters the viewport, then stop watching it.
 * Returns a disposer to cancel before it fires (e.g. on component destroy).
 *
 * One shared IntersectionObserver backs every caller: when a screenful of elements becomes visible at
 * once, the browser delivers them in a single callback, so all their `callback`s run in the same task.
 * That is what lets a downstream microtask-batched loader (see BatchedKeyLoader) coalesce them into one
 * request — a per-element observer fires in separate tasks and defeats the batch.
 */
let observer: IntersectionObserver | null = null;
const callbacks = new WeakMap<Element, () => void>();

function ensureObserver(): IntersectionObserver {

  if (observer) return observer;

  observer = new IntersectionObserver(entries => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;

      const callback = callbacks.get(entry.target);
      if (!callback) continue;

      callbacks.delete(entry.target);
      observer!.unobserve(entry.target);
      callback();
    }
  });

  return observer;
}

export function observeVisibleOnce(element: Element, callback: () => void): () => void {

  callbacks.set(element, callback);
  ensureObserver().observe(element);

  return () => {
    if (callbacks.has(element)) {
      callbacks.delete(element);
      observer?.unobserve(element);
    }
  };
}
